import Axios, { AxiosError, AxiosRequestConfig } from "axios";

// Deep imports (not the `@/contexts` barrel): the barrel pulls in every context
// provider and its hooks, which import back into `@/constants` / `@/utils` and
// would form an import cycle with the generated API modules that import this
// client — leaving their exports undefined at module-evaluation time.
import { getAccessToken, getRefreshToken } from "@/contexts/AuthContext/AuthContext.utils";
import { broadcastLogout } from "@/contexts/AuthContext/logoutBroadcast";
import { env } from "@/env";
import { stashAxiosErrorContext } from "@/lib/sentryAxios";
import { clearImpersonationSession, isImpersonating } from "@/utils/auth/impersonation";
import { getRequestAuthToken } from "@/utils/auth/requestAuthToken";
import { refreshAccessToken } from "@/utils/auth/tokenRefresh";

declare module "axios" {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface InternalAxiosRequestConfig {
    /** Set once a 401 on this request has already triggered a refresh + replay. */
    _authRetry?: boolean;
  }
}

export const restAxios = Axios.create({
  baseURL: env.backendUrl,
});

restAxios.interceptors.request.use(async (config) => {
  // Impersonation-aware, proactively refreshed — see `getRequestAuthToken`.
  const token = await getRequestAuthToken();

  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }

  config.headers.set("x-app-source", "dashboard");

  return config;
});

export const REST_UNAUTHORIZED_EVENT = "rest-unauthorized";

restAxios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      // A 401 while impersonating ends only the impersonation, leaving the
      // admin's real session (and other tabs) untouched — and never refreshes
      // the admin's underlying tokens.
      if (isImpersonating()) {
        clearImpersonationSession();
        window.location.reload();
        return Promise.reject(error);
      }

      // An access token that expired mid-flight is recoverable: refresh once
      // (single-flight, shared with the Apollo link) and replay the request.
      const config = error.config;

      if (config && !config._authRetry && getRefreshToken()) {
        config._authRetry = true;

        const token = await refreshAccessToken();

        if (token) {
          config.headers.set("Authorization", `Bearer ${token}`);
          return restAxios(config);
        }

        // Refresh rejected: `refreshAccessToken` already cleared the auth cells
        // and broadcast the logout to the OTHER tabs (a BroadcastChannel never
        // delivers to its sender), so this tab is signalled explicitly — the
        // `getAccessToken()` guard below can no longer see a token.
        window.dispatchEvent(new Event(REST_UNAUTHORIZED_EVENT));
        return Promise.reject(error);
      }

      if (getAccessToken()) {
        broadcastLogout();
        window.dispatchEvent(new Event(REST_UNAUTHORIZED_EVENT));
      }
      return Promise.reject(error);
    }

    // Stash a sanitized copy of the response body so Sentry's `beforeSend` can
    // attach it and fingerprint by endpoint + status. Awaited because export
    // endpoints return `Blob` bodies that must be read asynchronously.
    await stashAxiosErrorContext(error);

    return Promise.reject(error);
  }
);

export const restClient = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  const source = Axios.CancelToken.source();
  const promise = restAxios({ ...config, ...options, cancelToken: source.token }).then(
    ({ data }) => data
  );

  // Allows react-query to cancel in-flight requests on unmount.
  (promise as Promise<T> & { cancel?: () => void }).cancel = () => {
    source.cancel("Query was cancelled");
  };

  return promise;
};

export type ErrorType<Error> = AxiosError<Error>;
export type BodyType<BodyData> = BodyData;
